import { MaintenanceTicket, Vendor, Turn, Inspection, StandardOperatingProcedure, MaintenanceAttachment, InspectionSchedule, TurnTask, VendorReview, VendorAvailabilityWindow, AvailableSlot, Bill } from '@repo/shared-types';
import type { Note } from '@repo/shared-types/common/notes';
import type { VendorContract } from '@repo/shared-types';
import { apiTransport as router } from './transport';
import type { QueryFilters } from './transport';
import { getContext } from './core';
import { fileToBase64 } from '@/utils/fileUtils';

// Inspection photos: HOLLOW stubs (see getMaintenanceDispatch + hollow registries).
export const maintenanceApi = {
  tickets: {
    list: async (statusFilterOrFilters?: string | Record<string, string>) => {
      const filters: Record<string, string> =
        typeof statusFilterOrFilters === 'string'
          ? (statusFilterOrFilters ? { statusFilter: statusFilterOrFilters } : {})
          : (statusFilterOrFilters ?? {});
      return router.get<MaintenanceTicket[] | { data: MaintenanceTicket[]; total: number }>('/maintenance/tickets', undefined, Object.keys(filters).length ? filters : undefined);
    },
    get: async (id: string) => {
      const context = getContext();
      return router.get<MaintenanceTicket>(`/maintenance/tickets/${id}`, context);
    },
    create: async (ticket: Omit<MaintenanceTicket, 'id' | 'createdAt'>) => {
      const context = getContext();
      return router.post<MaintenanceTicket>('/maintenance/tickets', ticket, context);
    },
    update: async (id: string, updates: Partial<MaintenanceTicket>) => {
      const context = getContext();
      return router.patch<MaintenanceTicket>(`/maintenance/tickets/${id}`, updates, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/maintenance/tickets/${id}`, context);
    },
    getAvailableSlots: async (ticketId: string, params?: { from?: string; to?: string }) => {
      const context = getContext();
      return router.get<AvailableSlot[]>(`/maintenance/tickets/${ticketId}/available-slots`, context, params);
    },
    schedule: async (ticketId: string, body: { slotStart: string }) => {
      const context = getContext();
      return router.post<MaintenanceTicket>(`/maintenance/tickets/${ticketId}/schedule`, body, context);
    },
    approve: async (id: string, notes?: string) => {
      const context = getContext();
      return router.patch<MaintenanceTicket>(
        `/maintenance/tickets/${id}`,
        { status: 'Approved', approvalNotes: notes } as Partial<MaintenanceTicket>,
        context
      );
    },
    reject: async (id: string, notes: string) => {
      const context = getContext();
      return router.patch<MaintenanceTicket>(
        `/maintenance/tickets/${id}`,
        { status: 'Rejected', rejectionNotes: notes } as Partial<MaintenanceTicket>,
        context
      );
    },
    pmReleasePendingApproval: async (
      id: string,
      body: { reason: string; nextStatus?: 'In Progress' | 'Open' }
    ) => {
      const context = getContext();
      return router.post<MaintenanceTicket>(`/maintenance/tickets/${id}/pm-release-pending`, body, context);
    },
    /** POST /maintenance/tickets/:id/bill — completed WO → vendor bill (cents). */
    createBillFromTicket: async (
      ticketId: string,
      body: { vendorId: string; amountCents: number; overRepairThresholdApproved?: boolean }
    ) => {
      const context = getContext();
      return router.post<Bill>(`/maintenance/tickets/${ticketId}/bill`, body, context);
    },
    notes: {
      list: async (ticketId: string) => router.get<Note[]>(`/maintenance/tickets/${ticketId}/notes`),
      add: async (ticketId: string, content: string) => {
        const context = getContext();
        return router.post<Note>(`/maintenance/tickets/${ticketId}/notes`, { content }, context);
      },
      create: async (note: { workOrderId: string, content: string }) => {
        const context = getContext();
        return router.post<Note>(
          `/maintenance/tickets/${note.workOrderId}/notes`,
          { content: note.content },
          context
        );
      },
      update: async (noteId: string, content: string) => router.patch<Note>(`/maintenance/tickets/notes/${noteId}`, { content }),
      delete: async (id: string) => router.delete<{ success: boolean }>(`/maintenance/tickets/notes/${id}`)
    },
    attachments: {
      list: async (ticketId: string) => {
        return router.get<MaintenanceAttachment[]>(`/maintenance/tickets/${ticketId}/attachments`);
      },
      add: async (ticketId: string, file: File, description?: string) => {
        const context = getContext();
        const content = await fileToBase64(file);
        const payload = {content, description, name: file.name, type: file.type, size: file.size, entityType: 'Ticket' as const, entityId: ticketId, uploadedBy: context.currentUserId, folder: 'ticket-attachments'};
        return router.post<MaintenanceAttachment>(`/maintenance/tickets/${ticketId}/attachments`, payload, context);
      },
      delete: async (fileId: string) => {
        const context = getContext();
        return router.delete<{ success: boolean }>(`/maintenance/tickets/attachments/${fileId}`, context);
      }
    }
  },

  vendors: {
    list: async (filters?: Record<string, string>) => {
      const context = getContext();
      return router.get<Vendor[] | { data: Vendor[]; total: number }>('/maintenance/vendors', context, Object.keys(filters ?? {}).length ? filters : undefined);
    },
    get: async (id: string) => router.get<Vendor>(`/maintenance/vendors/${id}`),
    create: async (vendor: Omit<Vendor, 'id'>) => {
      const context = getContext();
      return router.post<Vendor>('/maintenance/vendors', vendor, context);
    },
    update: async (id: string, updates: Partial<Vendor>) => {
      const context = getContext();
      return router.patch<Vendor>(`/maintenance/vendors/${id}`, updates, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/maintenance/vendors/${id}`, context);
    },
    leaderboard: async (filters?: QueryFilters) => router.get<import('types').VendorLeaderboardEntry[]>('/maintenance/vendors/leaderboard', undefined, filters),
    trust: {
      listScreenings: async (vendorId: string) => {
        const context = getContext();
        return router.get<
          Array<{
            id: string;
            provider: string;
            packageType: string;
            status: string;
            reportId: string | null;
            requestedAt: string;
            completedAt: string | null;
            error: string | null;
            hasArchivedReport: boolean;
          }>
        >(`/maintenance/vendors/${vendorId}/screenings`, context);
      },
      listTaxSummaries: async (vendorId: string) => {
        const context = getContext();
        return router.get<
          Array<{
            id: string;
            taxYear: number;
            totalQualifyingPaymentsCents: string;
            excludedPaymentsCents: string;
            formType: string | null;
            filedAt: string | null;
            stripeLastReportingPushAt: string | null;
            stripeLastReportingPushStatus: string | null;
          }>
        >(`/maintenance/vendors/${vendorId}/tax-summaries`, context);
      },
    },
    notes: {
      list: async (vendorId: string) => router.get<import('types').Note[]>(`/maintenance/vendors/${vendorId}/notes`),
      create: async (note: { vendorId: string; content: string; category?: string; operatingOrganizationId?: string }) => {
        const { vendorId, ...body } = note;
        return router.post<import('types').Note>(`/maintenance/vendors/${vendorId}/notes`, body);
      },
      update: async (id: string, updates: Partial<{ content: string; category?: string }>) => router.patch<import('types').Note>(`/maintenance/vendors/notes/${id}`, updates),
      delete: async (id: string) => router.delete<{ success: boolean }>(`/maintenance/vendors/notes/${id}`)
    },
    reviews: {
      list: async (vendorId: string) => router.get<VendorReview[]>(`/maintenance/vendors/${vendorId}/reviews`)
    },
    availability: {
      list: async (vendorId: string) => {
        const context = getContext();
        return router.get<VendorAvailabilityWindow[]>(`/maintenance/vendors/${vendorId}/availability`, context);
      },
      create: async (vendorId: string, window: { dayOfWeek: number; startTime: string; endTime: string; effectiveFrom?: string; effectiveTo?: string }) => {
        const context = getContext();
        return router.post<VendorAvailabilityWindow>(`/maintenance/vendors/${vendorId}/availability`, window, context);
      },
      update: async (windowId: string, updates: Partial<{ dayOfWeek: number; startTime: string; endTime: string; effectiveFrom?: string; effectiveTo?: string }>) => {
        const context = getContext();
        return router.patch<VendorAvailabilityWindow>(`/maintenance/vendors/availability/${windowId}`, updates, context);
      },
      delete: async (windowId: string) => {
        const context = getContext();
        return router.delete<{ success: boolean }>(`/maintenance/vendors/availability/${windowId}`, context);
      },
    },
    contracts: {
      list: async (filters?: { vendorId?: string; propertyId?: string; status?: string }) => {
        const context = getContext();
        return router.get<VendorContract[]>('/vendors/contracts', context, filters);
      },
      get: async (id: string) => {
        const context = getContext();
        return router.get<VendorContract>(`/vendors/contracts/${id}`, context);
      },
      create: async (body: Omit<VendorContract, 'id' | 'createdAt' | 'updatedAt'>) => {
        const context = getContext();
        return router.post<VendorContract>('/vendors/contracts', body, context);
      },
      update: async (id: string, body: Partial<Omit<VendorContract, 'id' | 'vendorId' | 'createdAt' | 'updatedAt'>>) => {
        const context = getContext();
        return router.patch<VendorContract>(`/vendors/contracts/${id}`, body, context);
      },
      delete: async (id: string) => {
        const context = getContext();
        return router.delete<{ success: boolean }>(`/vendors/contracts/${id}`, context);
      },
    },
  },

  turns: {
    list: async () => {
      const context = getContext();
      return router.get<Turn[]>('/maintenance/turns', context);
    },
    get: async (id: string) => {
      const context = getContext();
      return router.get<Turn>(`/maintenance/turns/${id}`, context);
    },
    create: async (turn: Omit<Turn, 'id' | 'createdAt' | 'updatedAt'>) => {
      const context = getContext();
      return router.post<Turn>('/maintenance/turns', turn, context);
    },
    update: async (id: string, updates: Partial<Turn>) => router.patch<Turn>(`/maintenance/turns/${id}`, updates),
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/maintenance/turns/${id}`, context);
    },
    tasks: {
      list: async (turnId: string) => {
        const context = getContext();
        return router.get<TurnTask[]>(`/maintenance/turns/${turnId}/tasks`, context);
      },
      add: async (turnId: string, task: Omit<TurnTask, 'id' | 'createdAt' | 'updatedAt'>) => {
        const context = getContext();
        return router.post<TurnTask>(`/maintenance/turns/${turnId}/tasks`, task, context);
      },
      create: async (turnId: string, task: Omit<TurnTask, 'id' | 'createdAt' | 'updatedAt'>) => {
        const context = getContext();
        return router.post<TurnTask>(`/maintenance/turns/${turnId}/tasks`, task, context);
      },
      update: async (taskId: string, updates: Partial<TurnTask>) => {
        const context = getContext();
        return router.patch<TurnTask>(`/maintenance/turns/tasks/${taskId}`, updates, context);
      },
      delete: async (taskId: string) => {
        const context = getContext();
        return router.delete<{ success: boolean }>(`/maintenance/turns/tasks/${taskId}`, context);
      }
    }
  },

  inspections: {
    list: async (filters?: QueryFilters) => {
      const q = filters ? '?' + new URLSearchParams(filters as Record<string, string>).toString() : '';
      return router.get<Inspection[]>(`/maintenance/inspections${q}`, getContext());
    },
    create: async (inspection: Omit<Inspection, 'id' | 'createdAt' | 'updatedAt'>) => {
      const context = getContext();
      return router.post<Inspection>('/maintenance/inspections', inspection, context);
    },
    update: async (id: string, updates: Partial<Inspection>) => {
      const context = getContext();
      return router.patch<Inspection>(`/maintenance/inspections/${id}`, updates, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/maintenance/inspections/${id}`, context);
    },
    generatePDF: async (inspectionId: string) => {
      const context = getContext();
      return router.post<{ url: string }>(`/maintenance/inspections/${inspectionId}/generate-pdf`, {}, context);
    },
    photos: {
      list: async (inspectionId: string) =>
        router.get<MaintenanceAttachment[]>(`/maintenance/inspections/${inspectionId}/photos`, getContext()),
      add: async (inspectionId: string, file: File, description?: string) => {
        const { currentUserId } = getContext();
        const content = await fileToBase64(file);
        const payload = {content, description, name: file.name, type: file.type, size: file.size, entityType: 'Inspection' as const, entityId: inspectionId, uploadedBy: currentUserId, folder: 'inspection-attachments'};
        return router.post<MaintenanceAttachment>(`/maintenance/inspections/${inspectionId}/photos`, payload, getContext());
      },
      delete: async (fileId: string) => {
        const context = getContext();
        return router.delete<{ success: boolean }>(`/maintenance/inspections/photos/${fileId}`, context);
      }
    },
    schedules: {
      list: async (filters?: QueryFilters) => {
        const q = filters ? '?' + new URLSearchParams(filters as Record<string, string>).toString() : '';
        return router.get<InspectionSchedule[]>(`/compliance/inspections/schedules${q}`);
      },
      create: async (schedule: Omit<InspectionSchedule, 'id' | 'createdAt' | 'updatedAt' | 'lastRunAt'>) => {
        const context = getContext();
        return router.post<InspectionSchedule>('/compliance/inspections/schedules', schedule, context);
      },
      update: async (id: string, updates: Partial<InspectionSchedule>) => {
        const context = getContext();
        return router.patch<InspectionSchedule>(`/compliance/inspections/schedules/${id}`, updates, context);
      },
      delete: async (id: string) => {
        const context = getContext();
        return router.delete<{ success: boolean }>(`/compliance/inspections/schedules/${id}`, context);
      }
    }
  },

  sops: {
    list: async () => {
      const context = getContext();
      return router.get<StandardOperatingProcedure[]>('/maintenance/sops', context);
    },
    get: async (id: string) => router.get<StandardOperatingProcedure>(`/maintenance/sops/${id}`),
    create: async (sop: StandardOperatingProcedure) => {
      const context = getContext();
      return router.post<StandardOperatingProcedure>('/maintenance/sops', sop, context);
    },
    update: async (id: string, updates: Partial<StandardOperatingProcedure>) => {
      const context = getContext();
      return router.patch<StandardOperatingProcedure>(`/maintenance/sops/${id}`, updates, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/maintenance/sops/${id}`, context);
    },
    instances: {
      list: async (filters?: Record<string, string>) => router.get<import('@repo/shared-types/projects').SOPInstance[]>('/maintenance/sops/instances', undefined, filters),
      get: async (id: string) => router.get<import('@repo/shared-types/projects').SOPInstance>(`/maintenance/sops/instances/${id}`),
      create: async (instance: Record<string, unknown>) => {
        const context = getContext();
        return router.post<import('@repo/shared-types/projects').SOPInstance>('/maintenance/sops/instances', instance, context);
      },
      update: async (id: string, updates: Partial<Record<string, unknown>>) => router.patch<import('@repo/shared-types/projects').SOPInstance>(`/maintenance/sops/instances/${id}`, updates),
      delete: async (id: string) => router.delete<{ success: boolean }>(`/maintenance/sops/instances/${id}`)
    }
  }
};

